import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ProjectModal from '../components/ProjectModal';
import './Projects.css';
import { projects } from '../data/siteData';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');

  const categories = ['All', ...new Set(projects.map(p => p.category))];

  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div className="projects-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Our Projects</h1>
            <p>Showcasing Excellence in Construction</p>
          </motion.div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="projects-filter-section">
        <div className="container">
          <div className="filter-buttons">
            {categories.map((category, index) => (
              <motion.button
                key={category}
                className={`filter-btn ${activeFilter === category ? 'active' : ''}`}
                onClick={() => setActiveFilter(category)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="projects-grid-section">
        <div className="container">
          <div className="projects-grid-wrapper">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="project-grid-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                onClick={() => setSelectedProject(project)}
              >
                <div className="project-grid-image">
                  <img src={project.image} alt={project.title} loading="lazy" />
                  <div className="project-grid-overlay">
                    <span className="project-grid-category">{project.category}</span>
                    <button className="view-details-btn">View Details</button>
                  </div>
                </div>
                <div className="project-grid-content">
                  <h3>{project.title}</h3>
                  <p className="project-grid-location">📍 {project.location}</p>
                  <p className="project-grid-description">{project.description}</p>
                  <div className="project-grid-meta">
                    <span className="completion-badge">Completed {project.completionDate}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <motion.div {...fadeInUp} className="no-projects">
              <h3>No projects found in this category</h3>
              <p>Please select a different category</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="projects-stats bg-light">
        <div className="container">
          <motion.div {...fadeInUp} className="section-header">
            <h2>Our Track Record</h2>
            <p>Numbers that speak for themselves</p>
          </motion.div>

          <div className="projects-stats-grid">
            <motion.div {...fadeInUp} className="stat-box">
              <h3>500+</h3>
              <p>Projects Completed</p>
            </motion.div>
            <motion.div 
              {...fadeInUp} 
              transition={{ duration: 0.6, delay: 0.1 }}
              className="stat-box"
            >
              <h3>450+</h3>
              <p>Happy Clients</p>
            </motion.div>
            <motion.div 
              {...fadeInUp} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="stat-box"
            >
              <h3>15+</h3>
              <p>Years Experience</p>
            </motion.div>
            <motion.div 
              {...fadeInUp} 
              transition={{ duration: 0.6, delay: 0.3 }}
              className="stat-box"
            >
              <h3>98%</h3>
              <p>Client Satisfaction</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="projects-cta">
        <div className="container">
          <motion.div {...fadeInUp} className="projects-cta-content">
            <h2>Have a Project in Mind?</h2>
            <p>Let's bring your vision to life with our expertise and dedication</p>
            <div className="cta-buttons">
              <a href="/contact" className="btn btn-primary btn-large">
                Start Your Project
              </a>
              <a href="tel:+15551234567" className="btn btn-outline btn-large">
                Call for Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <ProjectModal 
          project={selectedProject} 
          onClose={() => setSelectedProject(null)} 
        />
      )}
    </div>
  );
};

export default Projects;
